import * as THREE from 'three';
import { CameraController } from './CameraController';
import { createLighting } from './Lighting';
import { detectQuality } from './PerformanceManager';
import { LabScene } from './LabScene';

export class SceneManager {
  readonly scene = new THREE.Scene();
  readonly camera = new THREE.PerspectiveCamera(44, 1, 0.1, 120);
  readonly renderer: THREE.WebGLRenderer;
  readonly controller: CameraController;
  readonly lab: LabScene;
  readonly quality = detectQuality();
  private observer: ResizeObserver;

  constructor(readonly container: HTMLElement) {
    this.scene.background = new THREE.Color(0x07101e);
    this.scene.fog = new THREE.FogExp2(0x07101e, 0.018);
    this.camera.position.set(15, 15, 18);
    this.renderer = new THREE.WebGLRenderer({ antialias: this.quality.mode !== 'Mobile', powerPreference: 'high-performance' });
    this.renderer.setPixelRatio(this.quality.pixelRatio);
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.05;
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.domElement.className = 'scene-canvas';
    container.append(this.renderer.domElement);
    this.controller = new CameraController(this.camera, this.renderer.domElement);
    this.scene.add(createLighting(this.quality.shadowSize));
    this.lab = new LabScene(this.quality);
    this.scene.add(this.lab);
    const floor = new THREE.Mesh(
      new THREE.PlaneGeometry(80, 80),
      new THREE.MeshStandardMaterial({ color: 0x0a1422, roughness: 0.88, metalness: 0.12 })
    );
    floor.rotation.x = -Math.PI / 2;
    floor.position.y = -0.05;
    floor.receiveShadow = true;
    this.scene.add(floor);
    const grid = new THREE.GridHelper(80, 80, 0x193854, 0x10243a);
    grid.position.y = 0;
    this.scene.add(grid);
    this.observer = new ResizeObserver(() => this.resize());
    this.observer.observe(container);
    this.resize();
  }

  render(delta: number): void {
    this.lab.update(delta);
    this.controller.update(this.camera);
    this.renderer.render(this.scene, this.camera);
  }

  private resize(): void {
    const { clientWidth: width, clientHeight: height } = this.container;
    this.renderer.setSize(width, height, false);
    this.camera.aspect = width / Math.max(1, height);
    this.camera.updateProjectionMatrix();
  }

  dispose(): void {
    this.observer.disconnect();
    this.controller.dispose();
    this.renderer.dispose();
    this.renderer.domElement.remove();
  }
}
